import React from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from './ui/sheet';
import { Filter } from 'lucide-react';

const MobileFilters = ({ 
  filters, 
  setFilters, 
  toggleEmploymentType, 
  toggleWorkplace,
  showStatus = false,
  showIndustry = false 
}) => {
  return (
    <div className="lg:hidden mb-4">
      <Sheet>
        <SheetTrigger asChild>
          <Button variant="outline" className="w-full">
            <Filter className="w-4 h-4 mr-2" />
            Filters
          </Button>
        </SheetTrigger>
        <SheetContent side="left" className="w-80 overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Filters</SheetTitle>
          </SheetHeader>
          
          <div className="mt-6 space-y-6">
            {/* Position */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Position</label>
              <Input 
                placeholder="Enter position" 
                value={filters.position}
                onChange={(e) => setFilters(prev => ({...prev, position: e.target.value}))}
                className="w-full"
              />
            </div>

            {/* Status - only show if enabled */}
            {showStatus && (
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">Status</label>
                <Select value={filters.status} onValueChange={(value) => setFilters(prev => ({...prev, status: value}))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="paused">Paused</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                    <SelectItem value="draft">Draft</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Skillset */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Skillset</label>
              <Input 
                placeholder="Enter skills" 
                value={filters.skillset}
                onChange={(e) => setFilters(prev => ({...prev, skillset: e.target.value}))}
                className="w-full"
              />
            </div>

            {/* Experience */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Experience</label>
              <Select value={filters.experience} onValueChange={(value) => setFilters(prev => ({...prev, experience: value}))}>
                <SelectTrigger>
                  <SelectValue placeholder="Select experience" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="junior">Junior (0-2 years)</SelectItem>
                  <SelectItem value="mid">Mid (2-5 years)</SelectItem>
                  <SelectItem value="senior">Senior (5+ years)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Employment Type */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Employment Type</label>
              <div className="grid grid-cols-2 gap-2">
                {['Full-time', 'Part-time'].map((type) => (
                  <Button
                    key={type}
                    onClick={() => toggleEmploymentType(type)}
                    variant={filters.employmentType.includes(type) ? "default" : "outline"}
                    size="sm"
                    className="text-xs"
                  >
                    {type}
                  </Button>
                ))}
              </div>
            </div>

            {/* Salary Expectations */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Salary Expectations, EUR</label>
              <Select value={filters.salaryExpectations} onValueChange={(value) => setFilters(prev => ({...prev, salaryExpectations: value}))}>
                <SelectTrigger>
                  <SelectValue placeholder="Select salary range" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="40-60">€40,000 - €60,000</SelectItem>
                  <SelectItem value="60-80">€60,000 - €80,000</SelectItem>
                  <SelectItem value="80-100">€80,000 - €100,000</SelectItem>
                  <SelectItem value="100+">€100,000+</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Workplace */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Workplace</label>
              <div className="grid grid-cols-3 gap-2">
                {['On-site', 'Hybrid', 'Remote'].map((type) => (
                  <Button
                    key={type}
                    onClick={() => toggleWorkplace(type)}
                    variant={filters.workplace.includes(type) ? "default" : "outline"}
                    size="sm"
                    className="text-xs"
                  >
                    {type}
                  </Button>
                ))}
              </div>
            </div>

            {/* Industry - only show if enabled */}
            {showIndustry && (
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">Industry</label>
                <Select value={filters.industry} onValueChange={(value) => setFilters(prev => ({...prev, industry: value}))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select industry" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="technology">Technology</SelectItem>
                    <SelectItem value="finance">Finance</SelectItem>
                    <SelectItem value="healthcare">Healthcare</SelectItem>
                    <SelectItem value="education">Education</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Languages */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Languages</label>
              <Select value={filters.languages} onValueChange={(value) => setFilters(prev => ({...prev, languages: value}))}>
                <SelectTrigger>
                  <SelectValue placeholder="Select languages" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="english">English</SelectItem>
                  <SelectItem value="german">German</SelectItem>
                  <SelectItem value="spanish">Spanish</SelectItem>
                  <SelectItem value="french">French</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Notice Period */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Notice Period</label>
              <Select value={filters.noticePeriod} onValueChange={(value) => setFilters(prev => ({...prev, noticePeriod: value}))}>
                <SelectTrigger>
                  <SelectValue placeholder="Select notice period" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="immediate">Immediate</SelectItem>
                  <SelectItem value="1-month">1 Month</SelectItem>
                  <SelectItem value="2-months">2 Months</SelectItem>
                  <SelectItem value="3-months">3 Months</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default MobileFilters;